import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import GroupIcon from '@mui/icons-material/Group';
import { ServerUserOut as ServerUser } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';

interface Props {
  users: ServerUser[];
  loading: boolean;
  togglingUser: string | null;
  onToggleAccess: (email: string, currentAccess: boolean) => void;
}

export default function ServerUsersTab({ users, loading, togglingUser, onToggleAccess }: Props) {
  const t = useTranslation();
  return (
    <Card
      sx={{
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <GroupIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('usersTab.title')}</Typography>
      </Box>
      {loading ? (
        <Box sx={{ p: 3 }}>
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : users.length === 0 ? (
        <Box sx={{ px: 3, pb: 3 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            {t('usersTab.empty')}
          </Typography>
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>
                {t('usersTab.col.user')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', width: 120 }}>
                {t('usersTab.col.access')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.auth0_email} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {u.auth0_email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant={u.has_access ? 'outlined' : 'contained'}
                    disabled={togglingUser === u.auth0_email}
                    onClick={() => onToggleAccess(u.auth0_email, u.has_access)}
                    sx={u.has_access
                      ? { borderColor: '#d4d4d8', color: 'text.secondary', fontSize: '0.75rem', textTransform: 'none' }
                      : { bgcolor: 'primary.main', fontSize: '0.75rem', textTransform: 'none', '&:hover': { bgcolor: 'primary.dark' } }
                    }
                  >
                    {u.has_access ? t('usersTab.revokeButton') : t('usersTab.activateButton')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
